'use client';

/**
 * RecoveryStats Component
 *
 * Displays total recovery statistics - how many tokens have been
 * rescued through the Fund Recovery tool.
 */
import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { Card, CardBody, Skeleton } from '@heroui/react';
import { Trophy, Coins, TrendingUp } from 'lucide-react';
import { motion } from 'motion/react';
import { useChainId } from 'wagmi';
import Image from 'next/image';
import { useTokenPricing } from '@/hooks/portfolio/useTokenPricing';
import { useRecoveryStats, calculateRecoveryValue } from '@/hooks/masterchef/useRecoveryStats';
import { tokenIndexes } from '@/constants/project-specific/tokens';
import type { RequiredAggregatorChainType } from '@/constants/project-specific/project';

/**
 * Look up token logo by address from the token indexes
 */
function getTokenLogo(address: string, chainId: number): string | null {
  // Only check if chainId is in our supported aggregator chains
  if (!(chainId in tokenIndexes)) return null;
  const tokens = tokenIndexes[chainId as RequiredAggregatorChainType];
  const addressLower = address.toLowerCase();
  for (const token of Object.values(tokens)) {
    if (token.address?.toLowerCase() === addressLower && token.logo) {
      return token.logo;
    }
  }
  return null;
}

// Format large numbers nicely
function formatNumber(num: number): string {
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(2)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(2)}K`;
  if (num >= 1) return num.toFixed(2);
  if (num >= 0.0001) return num.toFixed(4);
  return '<0.0001';
}
export default function RecoveryStats() {
  const $ = _c(34);
  const chainId = useChainId();
  let t0;
  if ($[0] !== chainId) {
    t0 = {
      chainId,
      enabled: true
    };
    $[0] = chainId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    stats,
    isLoading: isStatsLoading
  } = useRecoveryStats(t0);
  let t1;
  bb0: {
    if (!stats?.tokenTotals) {
      let t2;
      if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [];
        $[2] = t2;
      } else {
        t2 = $[2];
      }
      t1 = t2;
      break bb0;
    }
    let t2;
    if ($[3] !== stats.tokenTotals) {
      t2 = stats.tokenTotals.map(_temp);
      $[3] = stats.tokenTotals;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = t2;
  }
  const tokensForPricing = t1;
  const t2 = tokensForPricing as any;
  const t3 = tokensForPricing.length > 0;
  let t4;
  if ($[5] !== chainId || $[6] !== t2 || $[7] !== t3) {
    t4 = {
      tokens: t2,
      chainId,
      enabled: t3
    };
    $[5] = chainId;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    prices,
    isLoading: isPricingLoading
  } = useTokenPricing(t4);
  let t5;
  bb1: {
    if (!stats?.tokenTotals || !prices) {
      let t6;
      if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = {
          totalValueUsd: null,
          tokenValues: []
        };
        $[9] = t6;
      } else {
        t6 = $[9];
      }
      t5 = t6;
      break bb1;
    }
    let t6;
    if ($[10] !== prices || $[11] !== stats.tokenTotals) {
      t6 = calculateRecoveryValue(stats.tokenTotals, prices);
      $[10] = prices;
      $[11] = stats.tokenTotals;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    t5 = t6;
  }
  const {
    totalValueUsd,
    tokenValues
  } = t5;
  const isLoading = isStatsLoading || isPricingLoading;
  const hasRecoveries = stats && stats.totalRecoveries > 0;
  if (!isLoading && !hasRecoveries) {
    return null;
  }
  let t6;
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      scale: [1, 1.1, 1]
    };
    t7 = {
      duration: 2,
      repeat: Infinity
    };
    $[13] = t6;
    $[14] = t7;
  } else {
    t6 = $[13];
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="flex items-center gap-3"><motion.div animate={t6} transition={t7} className="p-2 bg-primary/20 rounded-lg"><Trophy className="w-6 h-6 text-warning" /></motion.div><div><h3 className="font-bold text-lg">Community Recoveries</h3><p className="text-sm text-foreground-500">Tokens rescued through Fund Recovery</p></div></div>;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="flex items-center gap-2 mb-1"><TrendingUp className="w-4 h-4 text-success" /><span className="text-xs text-foreground-500 uppercase tracking-wide">Recoveries</span></div>;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== isLoading || $[18] !== stats?.totalRecoveries) {
    t10 = <div className="p-3 bg-content2/50 rounded-xl">{t9}{isLoading ? <Skeleton className="h-8 w-16 rounded-md" /> : <p className="text-2xl font-bold text-foreground">{stats?.totalRecoveries ?? 0}</p>}</div>;
    $[17] = isLoading;
    $[18] = stats?.totalRecoveries;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="flex items-center gap-2 mb-1"><Coins className="w-4 h-4 text-warning" /><span className="text-xs text-foreground-500 uppercase tracking-wide">Value Rescued</span></div>;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== isLoading || $[22] !== totalValueUsd) {
    t12 = <div className="p-3 bg-content2/50 rounded-xl">{t11}{isLoading ? <Skeleton className="h-8 w-24 rounded-md" /> : <p className="text-2xl font-bold text-success">{totalValueUsd !== null ? `$${totalValueUsd.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}` : "--"}</p>}</div>;
    $[21] = isLoading;
    $[22] = totalValueUsd;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t10 || $[25] !== t12) {
    t13 = <div className="grid grid-cols-2 gap-4">{t10}{t12}</div>;
    $[24] = t10;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== chainId || $[28] !== isLoading || $[29] !== tokenValues) {
    t14 = tokenValues.length > 0 && <div className="space-y-2"><p className="text-xs text-foreground-400 uppercase tracking-wide">Tokens Recovered</p><div className="flex flex-wrap gap-2">{isLoading ? <><Skeleton className="h-8 w-24 rounded-full" /><Skeleton className="h-8 w-28 rounded-full" /><Skeleton className="h-8 w-20 rounded-full" /></> : tokenValues.map(t15 => {
          const {
            address,
            symbol,
            amountFormatted,
            valueUsd
          } = t15;
          const logo = getTokenLogo(address, chainId);
          return <div key={address} className="flex items-center gap-2 px-3 py-1.5 bg-content2 rounded-full text-sm">{logo && <Image src={logo} alt={symbol} width={20} height={20} className="rounded-full" unoptimized={true} />}<span className="font-medium">{formatNumber(parseFloat(amountFormatted))} {symbol}</span>{valueUsd !== null && <span className="text-foreground-400">(${formatNumber(valueUsd)})</span>}</div>;
        })}</div></div>;
    $[27] = chainId;
    $[28] = isLoading;
    $[29] = tokenValues;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t13 || $[32] !== t14) {
    t15 = <Card className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20"><CardBody className="gap-4">{t8}{t13}{t14}</CardBody></Card>;
    $[31] = t13;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  return t15;
}
function _temp(t) {
  return {
    address: t.address,
    symbol: t.symbol,
    decimals: t.decimals
  };
}